import React, {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import detectEthereumProvider from '@metamask/detect-provider';
import {
  ChainExtended,
  selectBridgeSlice,
  setSelectedFromChain,
  setSelectedToChain,
} from '../redux/slices/bridgeSlice';
import Modal from './common/Modal/Modal';
import { useAppDispatch, useAppSelector } from '../hooks';
import { WalletProvider } from '../datas/walletProviders';
import {
  connectWallet,
  selectWalletSlice,
  setConnectedWallet,
  setFromChainWalletProvider,
  setToChainWalletProvider,
} from '../redux/slices/walletSlice';

import './ModalConnectWallet.css';
import { setModal, setIsWalletApproved } from '../redux/slices/modalSlice';
import UnsupportedChainIdError from '../errors/UnsupportedChainIdError';
import ModalLogin from './Login/Login';
import KStadiumNeedLoginError from '../errors/KStadiumNeedLoginError';
import { PostLoginResponse } from '../services/kstadium.service';
import { setJwtTokens } from '../redux/slices/kstadiumSlice';

export interface ModalConnectProps {
  selectedChain?: ChainExtended;
  mode: false | 'from' | 'to';
  onRequestClose: () => void;
  reset?: () => void;
  doConfirmRef?: MutableRefObject<() => Promise<void>>;
}

const ModalConnectWallet: FC<ModalConnectProps> = ({
  mode,
  selectedChain,
  onRequestClose,
  reset,
  doConfirmRef,
}) => {
  const dispatch = useAppDispatch();
  const { toChainWalletProvider } = useAppSelector(selectWalletSlice);
  const { amountRange: chainWay } = useAppSelector(selectBridgeSlice);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const setSelectedChain = useMemo(
    () => (mode === `from` ? setSelectedFromChain : setSelectedToChain),
    [mode],
  );

  const setWalletProvider = useMemo(
    () =>
      mode === `from` ? setFromChainWalletProvider : setToChainWalletProvider,
    [mode],
  );

  // 메타마스크 지갑이 설치되어 있는지 detect
  const [isMetaMaskDetected, setIsMetaMaskDetected] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<WalletProvider>();

  const handleChooseWallet = useCallback(async () => {
    if (!selectedChain || !selectedProvider) {
      return;
    }

    dispatch(setWalletProvider(selectedProvider));

    try {
      const connectResult = await connectWallet({
        chain: selectedChain,
        provider: selectedProvider,
      });
      dispatch(setConnectedWallet(connectResult));
      dispatch(setSelectedChain(selectedChain));

      if (doConfirmRef) {
        requestAnimationFrame(() => doConfirmRef.current?.());
      }
      // if (mode === `from` && toChainWalletProvider) {
      //   dispatch(setDisconnectWallet(toChainWalletProvider.symbol));
      //   dispatch(setToChainWalletProvider(null));
      //   dispatch(setTokenTransferAmount(null));
      //   dispatch(setAmountRage(null));
      // }

      // if (reset) {
      //   reset();
      // }
      onRequestClose();
    } catch (e: any) {
      // 현재 지갑연결이 실행중에 있을때 메타마스크에서 떨어지는 pending 상태의 에러
      if (e.code === -32002) {
        dispatch(setIsWalletApproved(true));
      }
      if (e instanceof UnsupportedChainIdError) {
        onRequestClose();
        dispatch(setModal(`NetworkAlert`));
      } else if (e instanceof KStadiumNeedLoginError) {
        setShowLoginModal(true);
      }
    }
  }, [
    dispatch,
    doConfirmRef,
    onRequestClose,
    selectedChain,
    selectedProvider,
    setSelectedChain,
    setWalletProvider,
  ]);

  const walletSelectable = React.useMemo(() => {
    if (selectedProvider?.symbol === `METAMASK`) {
      return isMetaMaskDetected;
    }
    return true;
  }, [selectedProvider?.symbol, isMetaMaskDetected]);

  // 메타마스크 선택시 연결 가능여부 확인
  useEffect(() => {
    if (selectedProvider?.symbol === `METAMASK`) {
      detectEthereumProvider().then((ethereum) => {
        setIsMetaMaskDetected(!!ethereum);
      });
    }
  }, [selectedProvider?.symbol]);

  const onLoginSuccess = useCallback(
    (data: PostLoginResponse['data']) => {
      dispatch(setJwtTokens(data));
      handleChooseWallet();
    },
    [dispatch, handleChooseWallet],
  );

  if (showLoginModal) {
    return (
      <ModalLogin
        isShow
        onRequestClose={() => setShowLoginModal(false)}
        onSuccess={() => {
          // TODO : 해당 로직으로 인해 unmount warnning 발생함. 로직 개선 필요
          handleChooseWallet();
          onRequestClose();
        }}
        onFail={onRequestClose}
      />
    );
  }

  return (
    <>
      <Modal
        isShow={!!mode}
        title="Connect Wallet"
        onClose={onRequestClose}
        className="modal-connect-wallet"
        explanation="Please connect your wallet to the chain you chose."
        footerComponent={
          <button
            onClick={handleChooseWallet}
            className={`btn btn-block btn-primary btn-lg ${
              walletSelectable ? `` : `disabled`
            }`}
            disabled={!selectedProvider}
          >
            OK
          </button>
        }
      >
        {selectedChain?.walletProviders.map((walletProvider) => (
          <div className="wallet-wrap" key={walletProvider.symbol}>
            <div
              onClick={() => setSelectedProvider(walletProvider)}
              style={
                selectedProvider === walletProvider
                  ? { boxShadow: `0 1px 16px rgba(13, 127, 233, .4)` }
                  : {}
              }
              className="wallet-item"
            >
              <img src={walletProvider.logoUrl} alt={walletProvider.name} />
              <strong>{walletProvider.name}</strong>
            </div>
            <div className="ux-hint">
              You don&apos;t have Wallet?
              <br />
              Download one now
            </div>
            <a
              href={walletProvider.downloadUrl}
              className="btn btn-sm btn-link"
              target="_blank"
              rel="noreferrer"
            >
              <i className="icon icon-download" /> Download
            </a>
          </div>
        ))}
      </Modal>
    </>
  );
};

export default ModalConnectWallet;
